import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Shield,
  FileText,
  Download,
  RefreshCw,
  Search,
  Users,
  Lock,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Filter,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Compliance = () => {
  const { shouldShowData } = useDemoData();

  const { token, user } = useAuth();
  const [auditLogs, setAuditLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [filters, setFilters] = useState({
    action: '',
    userEmail: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build query params
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.userEmail) params.append('user_email', filters.userEmail);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      
      const [logsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/compliance/audit-logs?${params.toString()}`, { headers }),
        axios.get(`${API_URL}/compliance/audit-summary`, { headers })
      ]);
      
      setAuditLogs(logsRes.data.logs);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch compliance data');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (type) => {
    setDownloading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = type === 'soc2' ? 'report/pdf' : 'access-report/pdf';
      
      const response = await axios.get(`${API_URL}/compliance/${endpoint}`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CARTIE_AI_${type === 'soc2' ? 'SOC2' : 'Access'}_Report_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const getActionBadgeColor = (action) => {
    if (action.includes('LOGIN')) return 'bg-emerald-100 text-emerald-700';
    if (action.includes('CREATE')) return 'bg-green-100 text-green-700';
    if (action.includes('UPDATE') || action.includes('MODIFY')) return 'bg-amber-100 text-amber-700';
    if (action.includes('DELETE')) return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-700';
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleString();
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Compliance" description="Connect your cloud account to see real data here." />;
  }


  return (
    <div className="space-y-6" data-testid="compliance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Shield className="w-8 h-8 text-emerald-500" />
            SOC 2 Compliance
          </h1>
          <p className="text-slate-400 dark:text-slate-400 mt-1">
            Audit logs, access reports, and compliance documentation
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emerald-100 dark:bg-cyan-900/30">
                <FileText className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total Requests</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {summary?.total_requests?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emerald-100 dark:bg-blue-900/30">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Unique Users</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {summary?.unique_users || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <Lock className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Sensitive Actions</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {summary?.sensitive_actions || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/30">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Failed Requests</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {summary?.failed_requests || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Download Reports Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-emerald-200 dark:border-cyan-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-500" />
              SOC 2 Compliance Report
            </CardTitle>
            <CardDescription>
              Generate comprehensive audit report for SOC 2 Type II compliance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-slate-400 dark:text-slate-400">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Activity summary with statistics
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Access control & RBAC overview
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Security controls documentation
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Sensitive actions audit trail
              </li>
            </ul>
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full bg-cyan-600 hover:bg-cyan-700"
              onClick={() => downloadReport('soc2')}
              disabled={downloading}
            >
              <Download className="w-4 h-4 mr-2" />
              Download SOC 2 Report
            </Button>
          </CardFooter>
        </Card>

        <Card className="border-emerald-200 dark:border-blue-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-500" />
              Access Control Report
            </CardTitle>
            <CardDescription>
              Generate RBAC access audit report for compliance review
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-slate-400 dark:text-slate-400">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                User accounts and roles
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Role-based permissions matrix
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Account creation dates
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Access level documentation
              </li>
            </ul>
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full"
              variant="outline"
              onClick={() => downloadReport('access')}
              disabled={downloading}
            >
              <Download className="w-4 h-4 mr-2" />
              Download Access Report
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Audit Log Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Action Type</Label>
              <Select value={filters.action || 'all'} onValueChange={(v) => setFilters({...filters, action: v === 'all' ? '' : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="USER_LOGIN">User Login</SelectItem>
                  <SelectItem value="DATA_CREATE">Data Create</SelectItem>
                  <SelectItem value="DATA_UPDATE">Data Update</SelectItem>
                  <SelectItem value="DATA_DELETE">Data Delete</SelectItem>
                  <SelectItem value="SETTINGS_UPDATE">Settings Update</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>User Email</Label>
              <Input
                placeholder="Filter by email"
                value={filters.userEmail}
                onChange={(e) => setFilters({...filters, userEmail: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              />
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button onClick={fetchData}>
              <Search className="w-4 h-4 mr-2" />
              Apply Filters
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setFilters({ action: '', userEmail: '', startDate: '', endDate: '' });
                fetchData();
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Audit Log</CardTitle>
          <CardDescription>Immutable record of all system actions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Timestamp</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-400 dark:text-slate-400">User</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Action</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Path</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-400 dark:text-slate-400">IP Address</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Status</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-400 dark:text-slate-400">Duration</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-400">
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((log) => (
                    <tr key={log.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="py-3 px-4 text-sm text-slate-400 dark:text-slate-400">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {log.user_email || <span className="text-slate-400">anonymous</span>}
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={getActionBadgeColor(log.action)}>
                          {log.action}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-400 dark:text-slate-400 font-mono">
                        {log.method} {log.path}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-400 dark:text-slate-400 font-mono">
                        {log.ip_address}
                      </td>
                      <td className="text-center py-3 px-4">
                        {log.success ? (
                          <CheckCircle className="w-5 h-5 text-emerald-500 inline" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 inline" />
                        )}
                      </td>
                      <td className="text-right py-3 px-4 text-sm text-slate-400 dark:text-slate-400">
                        {log.duration_ms?.toFixed(1)}ms
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Compliance Badges */}
      <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Badge className="bg-cyan-600 text-white px-4 py-2 text-sm">SOC 2 Type II Ready</Badge>
            <Badge className="bg-emerald-600 text-white px-4 py-2 text-sm">GDPR Compliant</Badge>
            <Badge className="bg-blue-600 text-white px-4 py-2 text-sm">AES-256 Encryption</Badge>
            <Badge className="bg-amber-600 text-white px-4 py-2 text-sm">TLS 1.3</Badge>
            <Badge className="bg-slate-600 text-white px-4 py-2 text-sm">99.9% SLA</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Compliance;
